import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import AttendeesAndNextMeetingModal from '../../components/AttendeesAndNextMeetingModal';
import axiosInstance from '../../utils/axiosInstance';

function fmtDate(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(`${value}T00:00:00`));
}

function to12h(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  let hours = parseInt(h, 10);
  const modifier = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${m} ${modifier}`;
}

// How many days a date is from today -- "Today", "In N days" (upcoming), or
// "N days ago" (already passed).
function dayOffsetInfo(value) {
  if (!value) return null;
  const target = new Date(`${value}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / 86400000);
  if (diffDays === 0) return { text: 'Today', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' };
  if (diffDays > 0) return { text: `In ${diffDays} day${diffDays === 1 ? '' : 's'}`, className: 'border-indigo-200 bg-indigo-50 text-indigo-700' };
  const passed = Math.abs(diffDays);
  return { text: `${passed} day${passed === 1 ? '' : 's'} ago`, className: 'border-gray-200 bg-gray-50 text-gray-500' };
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#818cf8' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(99,102,241,0.15)' : 'none',
    fontSize: '0.8125rem',
    '&:hover': { borderColor: '#818cf8' },
  }),
};

function NewMeetingModal({ staffOptions, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [location, setLocation] = useState('');
  const [chairedBy, setChairedBy] = useState(null);
  const [notes, setNotes] = useState('');
  const [inviteNotes, setInviteNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!title.trim() || !meetingDate) {
      setError('Title and meeting date are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await axiosInstance.post('/admin/meetings', {
        title: title.trim(),
        meetingDate,
        meetingTime: meetingTime || null,
        location: location.trim() || null,
        chairedBy: chairedBy?.value || null,
        notes: notes.trim() || null,
        inviteNotes: inviteNotes.trim() || null,
      });
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create meeting.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-slate-800">New Meeting</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="e.g. Weekly Coordination Meeting"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Date *</label>
              <input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Time</label>
              <input type="time" value={meetingTime} onChange={(e) => setMeetingTime(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Location</label>
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Conference Room A"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Chaired By</label>
            <Select value={chairedBy} onChange={setChairedBy} options={staffOptions} isClearable placeholder="Select staff…" styles={selectStyles} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="General minutes / notes…"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Invite Notes</label>
            <textarea value={inviteNotes} onChange={(e) => setInviteNotes(e.target.value)} rows={3} placeholder="Agenda shown in the Generate Invite message (kept separate from the Notes above)…"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
          </div>
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={saving} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Creating…' : 'Create Meeting'}
          </button>
        </div>
      </div>
    </div>
  );
}

// "Dear A," / "Dear A and B," / "Dear A, B, and C," -- the Oxford comma before
// the last name is what keeps a 3+ list from reading like a run-on.
function buildSalutation(names) {
  if (names.length === 0) return 'Dear Team,';
  if (names.length === 1) return `Dear ${names[0]},`;
  if (names.length === 2) return `Dear ${names[0]} and ${names[1]},`;
  return `Dear ${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]},`;
}

function GenerateInviteModal({ meeting, allStaff, onClose }) {
  const [inviteNotes, setInviteNotes] = useState(meeting.inviteNotes || '');
  const [attendees, setAttendees] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [copiedMessage, setCopiedMessage] = useState(false);
  const [copiedEmails, setCopiedEmails] = useState(false);
  const [copiedNames, setCopiedNames] = useState(false);
  const [copiedSubject, setCopiedSubject] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get(`/admin/meetings/${meeting.id}`)
      .then((resp) => {
        if (cancelled) return;
        setInviteNotes(resp.data?.meeting?.inviteNotes || '');
        setAttendees(resp.data?.meeting?.attendees || []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [meeting.id]);

  const staffById = useMemo(() => {
    const map = new Map();
    allStaff.forEach((s) => map.set(s.staffId, s));
    return map;
  }, [allStaff]);

  // Recipients = exactly this meeting's added attendees -- not the wider
  // APM/PM/Coordinator pool, so the invite only goes to who was actually
  // added under "Attendees & Next Meeting" for this meeting.
  const recipients = useMemo(() => {
    return attendees.map((a) => {
      const staff = staffById.get(a.staffId);
      return {
        staffId: a.staffId,
        name: a.name,
        position: staff?.position || null,
        officeEmail: staff?.officeEmail || null,
        gmail: staff?.gmail || null,
      };
    });
  }, [attendees, staffById]);
  const recipientEmails = useMemo(
    () => recipients.map((s) => s.officeEmail || s.gmail).filter(Boolean),
    [recipients]
  );

  const subject = `Invitation to Attend — ${meeting.title}`;

  const message = useMemo(() => {
    const dateLine = `${fmtDate(meeting.meetingDate)}${meeting.meetingTime ? ` at ${to12h(meeting.meetingTime)}` : ''}`;
    const salutation = buildSalutation(attendees.map((a) => a.name));
    const lines = [
      salutation,
      '',
      'You are cordially invited to attend the following meeting:',
      '',
      `Meeting: ${meeting.title}`,
      `Date & Time: ${dateLine}`,
      meeting.location ? `Venue: ${meeting.location}` : null,
      meeting.chairedByName ? `Chaired by: ${meeting.chairedByName}` : null,
      '',
      'This meeting will be conducted in person. Kindly make the necessary arrangements to attend on time, as your presence and input are highly valued.',
      inviteNotes ? `\nAgenda:\n${inviteNotes}` : null,
      '',
      'Please confirm your attendance at your earliest convenience, or let us know if you are unable to attend.',
      '',
      'Thank you, and we look forward to your participation.',
    ].filter((line) => line !== null);
    return lines.join('\n');
  }, [meeting, inviteNotes, attendees]);

  const copyMessage = async () => {
    await navigator.clipboard.writeText(message);
    setCopiedMessage(true);
    setTimeout(() => setCopiedMessage(false), 2000);
  };
  const copyEmails = async () => {
    await navigator.clipboard.writeText(recipientEmails.join(', '));
    setCopiedEmails(true);
    setTimeout(() => setCopiedEmails(false), 2000);
  };
  const copyNames = async () => {
    await navigator.clipboard.writeText(recipients.map((s) => s.name).join(', '));
    setCopiedNames(true);
    setTimeout(() => setCopiedNames(false), 2000);
  };
  const copySubject = async () => {
    await navigator.clipboard.writeText(subject);
    setCopiedSubject(true);
    setTimeout(() => setCopiedSubject(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Generate Invite</h3>
            <p className="mt-0.5 text-xs text-gray-500">For this meeting's added attendees</p>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100" aria-label="Close">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-600">Recipients ({recipients.length})</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={copyNames}
                  disabled={recipients.length === 0}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {copiedNames ? 'Copied!' : 'Copy names'}
                </button>
                <button
                  type="button"
                  onClick={copyEmails}
                  disabled={recipientEmails.length === 0}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {copiedEmails ? 'Copied!' : 'Copy emails'}
                </button>
              </div>
            </div>
            {recipients.length === 0 ? (
              <p className="text-xs text-gray-400">No attendees added yet -- use "Attendees &amp; Next Meeting" to add some first.</p>
            ) : (
              <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50 p-2">
                {recipients.map((s) => (
                  <span
                    key={s.staffId}
                    title={s.officeEmail || s.gmail || 'No email on file'}
                    className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-600"
                  >
                    {s.name}
                    {s.position && <span className="text-gray-400"> · {s.position}</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-600">Subject</label>
              <button type="button" onClick={copySubject} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                {copiedSubject ? 'Copied!' : 'Copy subject'}
              </button>
            </div>
            <input
              readOnly
              value={subject}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 focus:outline-none"
            />
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-600">Email message</label>
              <button type="button" onClick={copyMessage} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                {copiedMessage ? 'Copied!' : 'Copy message'}
              </button>
            </div>
            <textarea
              readOnly
              value={loadingDetail ? 'Loading…' : message}
              rows={14}
              className="w-full resize-y rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-700 focus:outline-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Close</button>
        </div>
      </div>
    </div>
  );
}

export default function Meetings() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffOptions, setStaffOptions] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [manageAttendeesMeetingId, setManageAttendeesMeetingId] = useState(null);
  const [inviteMeeting, setInviteMeeting] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadMeetings = () => {
    setLoading(true);
    axiosInstance.get('/admin/meetings')
      .then((resp) => setMeetings(resp.data?.meetings || []))
      .catch(() => setMeetings([]))
      .finally(() => setLoading(false));
  };

  const deleteMeeting = async (meeting) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete this meeting?',
      html: `<p>"<b>${meeting.title}</b>" (${fmtDate(meeting.meetingDate)}) will be permanently deleted, along with:</p>
        <ul style="text-align:left; margin-top:8px; padding-left:20px;">
          <li>All attendee records</li>
          <li>All action items raised in this meeting</li>
          <li>Its next-meeting reminder on the calendar</li>
        </ul>
        <p style="margin-top:8px;">This cannot be undone.</p>`,
      showCancelButton: true,
      confirmButtonText: 'Delete Meeting',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeletingId(meeting.id);
    try {
      await axiosInstance.delete(`/admin/meetings/${meeting.id}`);
      setMeetings((prev) => prev.filter((m) => m.id !== meeting.id));
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Meeting deleted',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete meeting.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    axiosInstance.get('/admin/staff').then((resp) => {
      const staff = resp.data?.staff || [];
      const activeStaff = staff.filter((s) => (s.loginStatus || 'active') === 'active');
      setStaffOptions(activeStaff.map((s) => ({ value: s.staffId, label: s.name })));
      setAllStaff(activeStaff);
    }).catch(() => { setStaffOptions([]); setAllStaff([]); });
    loadMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DashboardLayout title="Meetings">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Meetings</h2>
          <p className="mt-1 text-sm text-gray-600">Meeting minutes, attendance, and action-item tracking.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          New Meeting
        </button>
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center text-sm text-gray-400">Loading…</div>
      ) : meetings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-12 text-center text-sm text-gray-400">No meetings yet.</div>
      ) : (
        <div className="space-y-3">
          {meetings.map((m) => {
            const meetingDayInfo = dayOffsetInfo(m.meetingDate);
            const nextDayInfo = dayOffsetInfo(m.nextMeetingDate);
            return (
            <div
              key={m.id}
              className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-indigo-200 hover:shadow-md"
            >
              <button
                onClick={() => navigate(`/admin/meetings/${m.id}`)}
                className="min-w-0 flex-1 text-left"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{m.title}</span>
                  {meetingDayInfo && (
                    <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${meetingDayInfo.className}`}>
                      {meetingDayInfo.text}
                    </span>
                  )}
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    {fmtDate(m.meetingDate)}{m.meetingTime ? ` · ${to12h(m.meetingTime)}` : ''}
                  </span>
                  {m.location && (
                    <span className="inline-flex items-center gap-1">
                      <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
                      {m.location}
                    </span>
                  )}
                  {m.chairedByName && (
                    <span className="inline-flex items-center gap-1">
                      <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
                      Chaired by {m.chairedByName}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" /></svg>
                    {m.presentCount}/{m.attendeeCount} attended
                  </span>
                </div>

                {m.nextMeetingDate && (
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-indigo-100 bg-indigo-50/70 px-2.5 py-1 text-[11px] font-medium text-indigo-700">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>
                    Next meeting: {fmtDate(m.nextMeetingDate)}{m.nextMeetingTime ? ` · ${to12h(m.nextMeetingTime)}` : ''}
                    {nextDayInfo && (
                      <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${nextDayInfo.className}`}>
                        {nextDayInfo.text}
                      </span>
                    )}
                  </div>
                )}
                {m.actionItemCount > 0 ? (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {m.pendingItemCount > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                        {m.pendingItemCount} pending
                      </span>
                    )}
                    {m.deadlineExtendedItemCount > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
                        {m.deadlineExtendedItemCount} deadline extended
                      </span>
                    )}
                    {m.completedItemCount > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        {m.completedItemCount} completed
                      </span>
                    )}
                    {m.carriedOverItemCount > 0 && (
                      <span
                        title="Raised here, but not completed before being carried into a later meeting"
                        className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-semibold text-gray-600"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                        {m.carriedOverItemCount} carried over
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="mt-1.5 text-[11px] text-gray-400">No action items raised yet.</p>
                )}
              </button>
              <button
                onClick={() => setManageAttendeesMeetingId(m.id)}
                title="Manage attendees and next meeting"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>
                Attendees &amp; Next Meeting
              </button>
              <button
                onClick={() => setInviteMeeting(m)}
                title="Generate a formal invite for this meeting's attendees"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                Generate Invite
              </button>
              <button
                onClick={() => deleteMeeting(m)}
                disabled={deletingId === m.id}
                title="Delete meeting"
                className="shrink-0 rounded-lg p-1.5 text-gray-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                aria-label="Delete meeting"
              >
                {deletingId === m.id ? (
                  <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                )}
              </button>
              <button
                onClick={() => navigate(`/admin/meetings/${m.id}`)}
                className="shrink-0 rounded-lg p-1.5 text-gray-300 hover:bg-gray-50 hover:text-gray-500"
                aria-label="View details"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
            );
          })}
        </div>
      )}

      {showNew && (
        <NewMeetingModal
          staffOptions={staffOptions}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); loadMeetings(); }}
        />
      )}

      {manageAttendeesMeetingId && (
        <AttendeesAndNextMeetingModal
          meetingId={manageAttendeesMeetingId}
          staffOptions={staffOptions}
          onClose={() => setManageAttendeesMeetingId(null)}
          onChanged={loadMeetings}
        />
      )}

      {inviteMeeting && (
        <GenerateInviteModal
          meeting={inviteMeeting}
          allStaff={allStaff}
          onClose={() => setInviteMeeting(null)}
        />
      )}
    </DashboardLayout>
  );
}
